<template>
  <div>
    <h1 slot="header" class="text-xl font-semibold mb-8">Tell us about your employer</h1>
    <div v-if="employerNotFound" class="items-start px-5 md:px-10">
      <div class="bg-white shadow-lg rounded-lg p-3">
        <div class="text-black text-lg">
          Uh oh! It looks like we had trouble finding your employer. Please provide your information
          below to see if we can service your employer.
        </div>

        <form autocomplete="off" class="w-full py-3 mt-4" novalidate @submit.prevent="next">
          <Input
            v-model="personalInformation.employer.value"
            :error="personalInformation.employer.error"
            :getter="$getters.employer"
            :maxlength="personalInformation.employer.maxlength"
            :name="personalInformation.employer.name"
            :id="personalInformation.employer.id"
            :placeholder="personalInformation.employer.placeholder"
            :setter="$setters.employer"
            type="text"
            :underlined="true"
            class="mb-3"
          />
          <Dropdown
            v-model="personalInformation.industry.value"
            v-bind:dropdownOptions="industries"
            :value="personalInformation.industry.value"
            :error="personalInformation.industry.error"
            :placeholder="personalInformation.industry.placeholder"
            :name="personalInformation.industry.name"
            type="text"
            :underlined="true"
            inputClass="mt-4 sm:mt-0"
            :id="personalInformation.industry.id"
            class="mb-3"
          />
          <Dropdown
            v-model="personalInformation.multipleDDAccounts.value"
            v-bind:dropdownOptions="['Yes', 'No']"
            :value="personalInformation.multipleDDAccounts.value"
            :error="personalInformation.multipleDDAccounts.error"
            :placeholder="personalInformation.multipleDDAccounts.placeholder"
            :name="personalInformation.multipleDDAccounts.name"
            type="text"
            :underlined="true"
            inputClass="mt-4 sm:mt-0"
            :id="personalInformation.multipleDDAccounts.id"
            class="mb-3"
          />

          <Dropdown
            v-model="personalInformation.directDepositUpdateDescription.value"
            v-bind:dropdownOptions="[
              'Paper form',
              'Online / Employee Self Service portal',
              'In person',
              'I don\'t know'
            ]"
            :value="personalInformation.directDepositUpdateDescription.value"
            :error="personalInformation.directDepositUpdateDescription.error"
            :placeholder="personalInformation.directDepositUpdateDescription.placeholder"
            :name="personalInformation.directDepositUpdateDescription.name"
            type="text"
            :underlined="true"
            inputClass="mt-4 sm:mt-0"
            :id="personalInformation.directDepositUpdateDescription.id"
            class="mb-3"
          />
        </form>
        <p class="underline text-blue-500 cursor-pointer" @click="changeIsNer()">
          Look for employers on our database
        </p>
      </div>
    </div>
    <div v-else class="flex h-full w-full items-center flex-col justify-center gap-6">
      <div
        v-if="alreadyHasEmployer"
        class="flex h-full w-full items-center justify-center flex-col gap-3"
      >
        <h1 class="font-semibold pb-4">Is the selected employer still your current employer?</h1>
        <b>{{ this.$store.getters.employerName }}</b>
        <p class="text-xs cursor-pointer text-blue-500 underline" @click="cleanCurrentEmployer()">
          Change employer
        </p>
      </div>
      <div v-else class="flex h-full w-full items-center flex-col justify-center">
        <p class="underline text-blue-500 cursor-pointer" @click="changeIsNer()">
          Don't see your employer? Click here
        </p>
        <AlgoliaSearch class="w-full" @employerObject="unpackEmployer" />
        <div class="flex items-center w-4/5 py-4">
          <button
            class="mt-1 border border-gray-500 bg-gray-300 w-6 h-6 focus:outline-none focus:border-primary px-2 flex items-center justify-center"
            aria-label="employerDisclosure"
            @click="employerDisclosure = !employerDisclosure"
          >
            <i v-if="employerDisclosure" class="fas fa-check text-sm font-bold text-green-500"></i>
          </button>
          <p class="ml-2 text-sm text-justify">
            Locating an employer on this list does not guarantee loan approval or that a specific
            employer is supported. Supported employers are subject to change at any time and loan
            approvals are dependent on a variety of factors.
          </p>
        </div>
      </div>
      <img alt="employer sketch" :src="img" class="w-3/5" />
    </div>
    <div class="flex w-full mt-4">
      <base-button
        :loading="nextLoading"
        type="primary ml-auto"
        :disabled="!nextEnabled"
        aria-label="navigate next"
        @click="handleNext()"
      >
        Next
      </base-button>
    </div>
  </div>
</template>

<script>
import AlgoliaSearch from '@/components/VIVA/AlgoliaSearch.vue'
import Dropdown from '@/components/VIVA/Dropdown.vue'
import gql from 'graphql-tag'

export default {
  name: 'ChooseEmployer',
  components: {
    Dropdown,
    AlgoliaSearch
  },
  data: () => ({
    employer: undefined,
    nextLoading: false,
    isNer: false,
    personalInformation: {
      employer: {
        name: 'Employer',
        placeholder: 'VIVA Finance',
        value: '',
        error: '',
        id: 'ner_employer'
      },
      industry: {
        name: 'Industry',
        placeholder: 'Education',
        value: '',
        error: '',
        id: 'ner_industry'
      },
      directDepositUpdateDescription: {
        name: 'How do you update your direct deposit information to receive your paycheck? (Optional)',
        placeholder: '',
        value: '',
        error: '',
        optional: true,
        id: 'ner_dd_description'
      },
      multipleDDAccounts: {
        name: 'Does your company allow for you to send your paycheck to multiple accounts?',
        placeholder: '',
        value: '',
        error: '',
        id: 'ner_dd_multiple_accounts'
      }
    },
    vivaIndustries: [
      'Accommodation and Food Services',
      'Administrative and Waste Services',
      'Early Education',
      'Government - Federal',
      'Government - Local',
      'Government - State',
      'Healthcare',
      'Higher Education',
      'K-12 Education',
      'Pest Control',
      'Power and Utilities'
    ],
    qsIndustries: [
      'Agriculture, Forestry, Fishing and Hunting',
      'Arts, Entertainment and Recreation',
      'Construction',
      'Finance and Insurance',
      'Information',
      'Management of Companies and Enterprises',
      'Manufacturing',
      'Mining',
      'Professional, Scientific, and Technical Services',
      'Real Estate and Rental Leasing',
      'Retail Trade',
      'Transportation and Warehousing',
      'Wholesale Trade'
    ],
    employerDisclosure: false
  }),
  methods: {
    unpackEmployer(employer) {
      this.employer = employer
      this.$store.commit('applicationData/updateEmployerFromAlgolia', employer)
    },
    async handleNext() {
      this.nextLoading = true

      const promise = this.isNer
        ? async () => {
            let requestParams = []
            for (const [key, value] of Object.entries(this.personalInformation)) {
              if (key === 'multipleDDAccounts')
                requestParams.push(`${key} : ${value.value === 'Yes'}`)
              else requestParams.push(`${key} : "${value.value}"`)
            }

            const mutationString = `mutation { addNewEmployerRequest(${requestParams.join(',')}) { 
                        nextRoute
                     } }`

            return await new Promise((res, rej) => {
              this.$apiClient
                .mutate({
                  mutation: gql(mutationString)
                })
                .then(({ data }) => {
                  const {
                    addNewEmployerRequest: { nextRoute }
                  } = data

                  const isVivaLoan = this.isVivaIndustry()

                  this.$store.commit('setNerIndustryType', isVivaLoan)

                  if (isVivaLoan) return res(nextRoute)

                  this.$apiClient
                    .mutate({
                      mutation: gql(`mutation { inTheHoleActionTaken }`)
                    })
                    .then(() => res(nextRoute))
                    .catch(rej)
                })
                .catch((e) => {
                  console.log(e)
                  Swal.fire({
                    title: 'Your request could not be recorded.',
                    confirmButtonColor: '#69D393',
                    text: 'An error occurred and we could not record your response. Please reach out to customer support for more help.',
                    icon: 'error',
                    confirmButtonText: `Ok`
                  })
                  rej(e)
                })
            })
          }
        : async () => {
            const outputData = {
              employerId: this.$store.getters.employerId
            }

            return await new Promise((res, rej) => {
              this.$store
                .dispatch('applicationData/setEmployer', outputData)
                .then(({ serverError, validationError, nextRoute, data }) => {
                  if (serverError) throw new Error(`Server Error: ${serverError}`)

                  if (validationError && validationError.length) {
                    validationError.forEach((invalidField) => {
                      this.employmentInformation[invalidField].error = 'Invalid value'
                    })

                    throw new Error('Validation Error')
                  }

                  if (data && JSON.parse(data).error) throw new Error(JSON.parse(data).error)

                  return res(nextRoute)
                })
                .catch(rej)
            })
          }

      promise()
        .then((nextRoute) => {
          window.scrollTo({ top: 0, behavior: 'smooth' })
          this.$emit('next', nextRoute)
        })
        .catch((e) => {
          console.log(`FE Error in applicationData/setEmployer: ${e}`)
          console.warn(e)
        })
        .finally(() => {
          this.nextLoading = false
        })
    },
    handleBack() {
      window.scrollTo({ top: 0, behavior: 'smooth' })
      this.$emit('back')
    },
    cleanCurrentEmployer() {
      this.employer = undefined
      this.$store.commit('applicationData/clearCurrentSelectedEmployerFromAlgolia')
    },
    changeIsNer() {
      this.isNer = !this.isNer
    },
    isVivaIndustry() {
      const bool = [...this.vivaIndustries, '--Other--'].includes(
        this.personalInformation.industry.value
      )
      console.log('bool: ', bool)
      return bool
    }
  },
  computed: {
    employerNotFound() {
      return this.isNer
    },
    alreadyHasEmployer() {
      return this.$store.getters.employerId && !this.employer
    },
    nextEnabled() {
      const currentEmployerAndDisclosure = this.employer
        ? this.employer && this.employerDisclosure
        : !!this.$store.getters.employerId

      return (
        (!this.isNer && currentEmployerAndDisclosure) ||
        (this.isNer &&
          Object.keys(this.personalInformation).every(
            (k) => this.personalInformation[k].value && !this.personalInformation[k].error
          ))
      )
    },
    img() {
      return require('@/assets/img/sketches/employers.svg')
    },
    industries() {
      const allIndustries = [...this.vivaIndustries, ...this.qsIndustries].sort()

      return ['--Other--', ...allIndustries]
    }
  }
}
</script>
